//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import angular, { IScope } from 'angular';
import React, { FC, useRef } from 'react';
import styled from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import VirtualizedContentCardGrid from '@components/organisms/content-card-grid/VirtualizedContentCardGrid';
import { UploaderDisclaimer } from '@components/uploader-disclaimer/UploaderDisclaimer';
import { QueuedUpload } from '@legacy-components/common/queued-upload/QueuedUpload';
import makeAngularReactControllerWithScope, {
  applyAsyncScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { Mixins } from '@theme/mixins';
import { itemToPartialCuid } from '@utils/partialCuidUtils';

import { LibraryGridCard } from 'app/components/content/library-grid-content/LibraryGridCard';
import {
  GRID_CONTENT_CARD_HEIGHT,
  GRID_CONTENT_CARD_HEIGHT_NO_METADATA,
  GRID_CONTENT_CARD_WIDTH,
  ViewMode,
} from 'app/components/content/library-grid-content/constants';

interface Props {
  items: IContentLibraryItem[];
  isRefreshingContent: boolean;
  fetchMoreContent: () => void;
  scrollContainer: string;
  viewMode: ViewMode;
  endOfItemsReached: boolean;
  isLoadingInitialContent: boolean;
  queuedUploads?: string[];
}

const renderItem = (item: IContentLibraryItem, viewMode: ViewMode) => {
  if (item) {
    return (
      <LibraryGridCard
        key={item.id}
        disableMetadata={viewMode === 'grid'}
        item={item}
      />
    );
  } else {
    return null;
  }
};

const renderLoadingItem = (uuid: string) => {
  return (
    <LoadingSheet key={uuid}>
      <QueuedUpload uuid={uuid} />
    </LoadingSheet>
  );
};

export const LibraryGridContent = ({
  endOfItemsReached,
  fetchMoreContent,
  isLoadingInitialContent,
  isRefreshingContent,
  items,
  queuedUploads,
  scrollContainer,
  viewMode,
}: Props) => {
  const cardHeght =
    viewMode === 'grid'
      ? GRID_CONTENT_CARD_HEIGHT_NO_METADATA
      : GRID_CONTENT_CARD_HEIGHT;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const orderedPartialCuids = items.map((item) =>
    itemToPartialCuid(item.itemType, item.id),
  );

  if (isLoadingInitialContent) {
    return <CenteredSpinner />;
  }

  return (
    <LibraryGridContentContainer ref={containerRef}>
      <VirtualizedContentCardGrid
        cardHeight={cardHeght}
        cardWidth={GRID_CONTENT_CARD_WIDTH}
        includeCardPadding={true}
        isRefreshingContent={isRefreshingContent}
        onBottomReached={fetchMoreContent}
        orderedPartialCuids={orderedPartialCuids}
        postFixConfig={{
          shouldShowPostFixComponent: true,
          postFixComponent: endOfItemsReached ? (
            <VirtualizedContentCardGrid.EndOfContent />
          ) : (
            <VirtualizedContentCardGrid.LoadingSpinner />
          ),
        }}
        scrollContainerRef={containerRef}
        scrollContainerSelector={scrollContainer}
      >
        {queuedUploads?.map((uuid) => renderLoadingItem(uuid))}
        {items.map((item) => renderItem(item, viewMode))}
      </VirtualizedContentCardGrid>
      <UploaderDisclaimer />
    </LibraryGridContentContainer>
  );
};

angular.module('curalate').component('libraryGridContent', {
  bindings: {
    queuedUploads: '<',
    endOfItemsReached: '<',
    items: '<',
    scrollContainer: '<',
    fetchMoreContent: '<',
    isRefreshingContent: '<',
    viewMode: '<',
    isLoadingInitialContent: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: Props } & IScope) => {
      return (
        <LibraryGridContent
          endOfItemsReached={scope.$ctrl.endOfItemsReached}
          fetchMoreContent={applyAsyncScope(
            scope,
            scope.$ctrl.fetchMoreContent,
          )}
          isLoadingInitialContent={scope.$ctrl.isLoadingInitialContent}
          isRefreshingContent={scope.$ctrl.isRefreshingContent}
          items={scope.$ctrl.items}
          queuedUploads={scope.$ctrl.queuedUploads}
          scrollContainer={scope.$ctrl.scrollContainer}
          viewMode={scope.$ctrl.viewMode}
        />
      );
    },
  ),
});

const LibraryGridContentContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const LoadingSheet = styled.div`
  ${Mixins.Sheet.Low}

  height: ${GRID_CONTENT_CARD_HEIGHT}px;
  width: ${GRID_CONTENT_CARD_WIDTH}px;
`;
